import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera as CameraIcon, X } from "lucide-react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
  ActionButton,
  FieldGroup,
  FormActions,
  FormSection,
  FormStack,
  TextAreaField,
  TextInputField,
} from "@shared/ui/forms/FormFields";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { SelectableCardList } from "@shared/ui/forms/SelectableCardList";
import { toast } from "@shared/ui/feedback/Toast";
import { platform } from "@shared/lib/platform";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { fetchMyProperties } from "@features/properties/api";
import {
  createSupportRequest,
  fetchSupportTopics,
  uploadSupportRequestPhoto,
  type SupportPriority,
} from "./api";

const PRIORITY_OPTIONS: Array<{ value: SupportPriority; label: string }> = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
import { pageCopy } from "@shared/lib/pageCopy";
import { focusNextOnEnter, applyEnterKeyHint } from "@shared/lib/formNav";
import { LocationPinPicker, type PinCoords } from "./LocationPinPicker";

type LocationMode = "property" | "pin";

/**
 * New support request — grouped shared-form layout that matches the resident
 * app's newer forms. Request details stay in one card, with location and photo
 * handled in their own focused sections.
 */
export function NewSupportRequest(): JSX.Element {
  const copy = pageCopy().support.new;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [topicId, setTopicId] = useState("");
  const [subTopicId, setSubTopicId] = useState("");
  const [locationMode, setLocationMode] = useState<LocationMode>("property");
  const [propertyId, setPropertyId] = useState("");
  const [pin, setPin] = useState<PinCoords | null>(null);
  const [photo, setPhoto] = useState<{ blob: Blob; preview: string } | null>(
    null,
  );
  const [photoBusy, setPhotoBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<SupportPriority>("normal");

  const topicsQuery = useQuery({
    queryKey: ["support-topics"],
    queryFn: fetchSupportTopics,
  });
  const propertiesQuery = useQuery({
    queryKey: ["my-properties"],
    queryFn: fetchMyProperties,
  });

  const topics = topicsQuery.data ?? [];
  const selectedTopic = topics.find((topic) => topic.topic_id === topicId);

  const submit = useMutation({
    mutationFn: async () => {
      const pinned = locationMode === "pin" ? pin : null;
      let body = description.trim();
      if (pinned) {
        body += `\n\n— Pinned location —\nhttps://www.google.com/maps?q=${pinned.lat},${pinned.lng}`;
      }
      const created = await createSupportRequest({
        topic_id: topicId,
        subtopic_id: subTopicId || undefined,
        subject: subject.trim(),
        description: body,
        priority,
        location_id:
          locationMode === "property" ? propertyId || undefined : undefined,
        latitude: pinned?.lat,
        longitude: pinned?.lng,
      });
      if (photo) {
        try {
          await uploadSupportRequestPhoto(created.request_id, photo.blob);
        } catch {
          toast.danger(
            "Photo not attached",
            "Your request was submitted without the photo.",
          );
        }
      }
      return created;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["support-requests"] });
      toast.success(
        "Request submitted",
        "The estate team will pick this up shortly.",
      );
      navigate("/app/support/requests", { replace: true });
    },
    onError: () => toast.danger("Could not submit", "Please try again."),
  });

  const pickPhoto = async () => {
    setPhotoBusy(true);
    try {
      let blob: Blob;
      if (platform.isNative()) {
        const shot = await Camera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
        });
        if (!shot.webPath) return;
        blob = await fetch(shot.webPath).then((r) => r.blob());
      } else {
        const file = await new Promise<File | null>((resolve) => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = () => resolve(input.files?.[0] ?? null);
          input.click();
        });
        if (!file) return;
        blob = file;
      }
      setPhoto((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return { blob, preview: URL.createObjectURL(blob) };
      });
    } catch (e) {
      toast.danger(
        "Couldn't add photo",
        e instanceof Error ? e.message : undefined,
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = () => {
    setPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
  };

  const canSubmit =
    !!topicId && subject.trim().length > 0 && description.trim().length > 0;

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        backTo="/app/support/requests"
        backBehavior="parent"
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        titleClassName="whitespace-pre-line leading-tight"
      />

      <FormStack className="pt-5">
        <div
          className="flex flex-col gap-5"
          onKeyDown={focusNextOnEnter}
          onFocus={applyEnterKeyHint}
        >
          <FormSection
            title="Request details"
            description="Choose the right category and describe what the estate team needs to fix."
          >
            <FieldGroup label="Category">
              {topicsQuery.isLoading ? (
                <Skeleton className="h-9" />
              ) : topics.length === 0 ? (
                <p className="text-body-sm text-ink-muted">
                  No categories available.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic) => {
                    const on = topicId === topic.topic_id;
                    return (
                      <button
                        key={topic.topic_id}
                        type="button"
                        onClick={() => {
                          setTopicId(topic.topic_id);
                          setSubTopicId("");
                        }}
                        className={
                          "rounded-pill border px-3.5 py-1.5 text-body-sm transition-colors " +
                          (on
                            ? "border-primary-600 bg-primary-700 text-white"
                            : "border-hairline-strong bg-elevated text-ink")
                        }
                      >
                        {topic.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </FieldGroup>

            {selectedTopic && selectedTopic.subtopics.length > 0 ? (
              <FieldGroup label="Sub-category" description="Optional">
                <div className="flex flex-wrap gap-2">
                  {selectedTopic.subtopics.map((sub) => {
                    const on = subTopicId === sub.subtopic_id;
                    return (
                      <button
                        key={sub.subtopic_id}
                        type="button"
                        onClick={() => setSubTopicId(on ? "" : sub.subtopic_id)}
                        className={
                          "rounded-pill border px-3.5 py-1.5 text-body-sm transition-colors " +
                          (on
                            ? "border-primary-600 bg-primary-700 text-white"
                            : "border-hairline-strong bg-elevated text-ink")
                        }
                      >
                        {sub.name}
                      </button>
                    );
                  })}
                </div>
              </FieldGroup>
            ) : null}

            <TextInputField
              label="Short title"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Leaking tap in main bathroom"
              required
            />

            <TextAreaField
              label="What's the issue?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Describe what's happening, where, and since when."
              required
            />

            <FieldGroup label="Priority" description="How urgent is this?">
              <div className="flex flex-wrap gap-2">
                {PRIORITY_OPTIONS.map((opt) => {
                  const on = priority === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPriority(opt.value)}
                      className={
                        "rounded-pill border px-3.5 py-1.5 text-body-sm transition-colors " +
                        (on
                          ? "border-primary-600 bg-primary-700 text-white"
                          : "border-hairline-strong bg-elevated text-ink")
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </FieldGroup>
          </FormSection>

          <FormSection
            title="Location"
            description="Choose one of your properties or pin the exact spot on the map."
          >
            <FieldGroup label="How should we locate this issue?">
              <div className="flex gap-1 rounded-pill bg-sunken p-1">
                {(
                  [
                    ["property", "My property"],
                    ["pin", "Pin a location"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setLocationMode(id)}
                    className={
                      "flex-1 rounded-pill py-2 text-body-sm font-medium transition-colors " +
                      (locationMode === id
                        ? "bg-card text-ink shadow-card"
                        : "text-ink-secondary")
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FieldGroup>

            {locationMode === "property" ? (
              <FieldGroup label="Property">
                {propertiesQuery.isLoading ? (
                  <Skeleton className="h-12" />
                ) : (propertiesQuery.data ?? []).length > 0 ? (
                  <SelectableCardList
                    items={propertiesQuery.data!.map((property) => ({
                      id: property.property_id,
                      title: property.unit_label,
                      subtitle: [property.type_name, property.address]
                        .filter(Boolean)
                        .join(" · "),
                    }))}
                    selectedId={propertyId}
                    onSelect={(id) =>
                      setPropertyId(id === propertyId ? "" : id)
                    }
                  />
                ) : (
                  <p className="text-body-sm text-ink-muted">
                    No properties linked. Pin the location on the map instead.
                  </p>
                )}
              </FieldGroup>
            ) : (
              <FieldGroup label="Pinned location">
                <LocationPinPicker value={pin} onChange={setPin} />
              </FieldGroup>
            )}
          </FormSection>

          <FormSection
            title="Photo"
            description="Add an optional photo if it helps the estate team understand the issue faster."
          >
            {photo ? (
              <div className="flex items-center gap-3 rounded-md border border-hairline-strong bg-elevated px-3.5 py-3">
                <img
                  src={photo.preview}
                  alt="Attached"
                  className="h-20 w-20 shrink-0 rounded-md object-cover hairline"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-body-sm font-semibold text-ink">
                    Photo attached
                  </p>
                  <p className="text-caption text-ink-muted">
                    Sent with your request.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={removePhoto}
                  aria-label="Remove photo"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink-secondary"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void pickPhoto()}
                disabled={photoBusy}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong bg-elevated py-3.5 text-body-sm font-medium text-ink-secondary transition-colors disabled:opacity-60"
              >
                <CameraIcon className="h-4.5 w-4.5" strokeWidth={1.5} />
                {photoBusy ? "Adding…" : "Add a photo"}
              </button>
            )}
          </FormSection>

          <FormActions sticky>
            <ActionButton
              disabled={!canSubmit}
              loading={submit.isPending}
              onClick={() => submit.mutate()}
            >
              Submit request
            </ActionButton>
          </FormActions>
        </div>
      </FormStack>
    </div>
  );
}
